import { LoadingOutlined } from "@ant-design/icons";
import { FormDrawer, buildInitialValues, buildValidationSchema } from "Components";
import { withFormik } from "formik";
import moment from "moment-hijri";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateNewAppMutation, useGetPersonalInformationMutation } from "services/auth";
import {
	useGetCitiesQuery,
	useGetFamilySizeQuery,
	useGetMaritalStatusLookupQuery,
	useGetNationalitiesQuery,
	useGetRegionsQuery,
	useGetTitlesQuery
} from "services/lookupSelector";
import { useTranslation } from "../../../contexts/TranslationContext";
import { PATCH_PERSONAL_DETAILS, SET_ACTIVE_STEP_INDEX, SET_EDIT_APPL_LOAD_FLAGS, SET_STEP_VALIDITY, useCreateRequestContext } from "./CreateRequestContext";
import { personalDetailsSchema } from "./schemas";

const PersonalDetails = withFormik({
	mapPropsToValues: () => buildInitialValues(personalDetailsSchema({
		buildingNumFlag: false,
		streetNameFlag: false,
		district_nameFlag: false,
		additionalNumberFlag: false,
		postCodeFlag: false,
		maritalStatus: [], nationalities: [], titleOptions: [], dependentOptions: []
	})),
	validationSchema: buildValidationSchema(personalDetailsSchema({
		buildingNumFlag: false,
		streetNameFlag: false,
		district_nameFlag: false,
		additionalNumberFlag: false,
		postCodeFlag: false,
		maritalStatus: [], nationalities: [], titleOptions: [], dependentOptions: []
	})),
	enableReinitialize: true,
	validateOnChange: true,
	validateOnMount: true,
	// validateOnSubmit: true
	// validateOnBlur:true
	// validateOnBlur: true,
})((props) => {
	const navigate = useNavigate();
	const { state, dispatch } = useCreateRequestContext();
	const [fetchPersonalInfo, { isLoading }] = useGetPersonalInformationMutation();
	const [createNewApp] = useCreateNewAppMutation();

	// RTK Query hooks for lookup data
	const { data: nationalitiesData, isLoading: nationalitiesLoading } = useGetNationalitiesQuery({ status: 'ACTIVE' });
	const { data: titlesData, isLoading: titlesLoading } = useGetTitlesQuery({ status: 'ACTIVE' });
	const { data: familySizeData, isLoading: familySizeLoading } = useGetFamilySizeQuery({ status: 'ACTIVE' });
	const { data: maritalStatusData, isLoading: maritalStatusLoading } = useGetMaritalStatusLookupQuery({ status: 'ACTIVE' });
	const { data: regionsData, isLoading: regionsLoading } = useGetRegionsQuery({ status: 'ACTIVE' });
	const { data: citiesData, refetch: refetchCities } = useGetCitiesQuery(
		{ REGIONREFERENCE: props?.values?.region, status: 'ACTIVE' },
		{ skip: !props?.values?.region }
	);

	const [nationalities, setNationalities] = useState([]);
	const [maritalStatus, setMaritalStatus] = useState([]);
	const [regions, setRegions] = useState([]);
	const [cities, setCities] = useState([]);
	const [dependentOptions, setDependentOptions] = useState([]);
	const [titleOptions, setTitleOptions] = useState([]);
	const { translate } = useTranslation();

	// Check if lookup data is still loading
	const initialDataLoading = nationalitiesLoading || titlesLoading || familySizeLoading || maritalStatusLoading || regionsLoading;
	const [personalInfoLoading, setPersonalInfoLoading] = useState(true)
	const [fieldFlags, setFieldFlags] = useState({
		buildingNumFlag: false,
		streetNameFlag: false,
		district_nameFlag: false,
		additionalNumberFlag: false,
		postCodeFlag: false,
	});
	const [valuesFilled, setValuesFilled] = useState(false);

	useEffect(() => {
		// Update flags after personal info is fetched
		const updateFlags = () => {
			setFieldFlags({
				buildingNumFlag: !!props?.values?.buildingNum,
				streetNameFlag: !!props?.values?.streetName,
				district_nameFlag: !!props?.values?.district_name,
				additionalNumberFlag: !!props?.values?.additionalNum,
				postCodeFlag: !!props?.values?.postCode,
			});
		};

		updateFlags();
	}, [personalInfoLoading]);


	const getUserId = () => {
		let user = null;
		try {
			user = JSON.parse(localStorage["@@TAMWEEL_USER"]);
		} catch (e) {
			navigate("/auth/sign-in");
			toast.error("Unauthorized. Please sign-in again.");
		}
		return user
	}

	const getPersonalInfoAsync = async (user) => {
		// let user;
		// try {
		// 	user = JSON.parse(localStorage["@@TAMWEEL_USER"]);
		// } catch (e) {
		// 	navigate("/auth/sign-in");
		// 	toast.error("Unauthorized. Please sign-in again.");
		// }

		if (!user) {
			navigate("/auth/sign-in");
			return;
		}

		const response = await fetchPersonalInfo({
			id: String(user?.USER_ID),
		})
			.unwrap()
			.catch((res) => {
				// console.log("Personal info", res);
			});
		if (response) {
			const info = response?.payload?.data?.info;
			const address = response?.payload?.data?.address?.personNationalAddressInfo?.Addresses?.[0];
			const latlong = address?.ObjLatLng || '';
			const [lat, long] = latlong.trim().split(/\s+/).map(Number);

			props.setValues((prevValues) => ({
				...prevValues,
				fullName: `${info[0].FIRST_NAME} ${info[0].MIDDLE_NAME} ${info[0].LAST_NAME}`,
				address: `${address?.BuildingNumber}, ${address?.Street_L2}  ${address?.District_L2}`,
				// additionalNumber: address?.AdditionalNumber,
				idExpiryDate: moment(info[0].ID_EXPIRY_DATE).format("YYYY-MM-DD"),
				postCode: address?.PostCode,
				buildingNum: address?.BuildingNumber,
				lat: lat,
				long: long,
				streetName: address?.Street,
				district_name: address?.District,
				additionalNum: address?.AdditionalNumber,
			}));
			setPersonalInfoLoading(false) //when personalInfo has finished loading
		}
	};

	const initializeData = async (user) => {
		try {
			// Process nationalities data
			if (nationalitiesData?.payload?.rows) {
				setNationalities(
					nationalitiesData.payload.rows
						.filter(nationality => {
							// Hide all nationalities if user is saudi and show only saudi arabia
							if (user && String(user.USER_ID)?.[0] === '1') {
								return String(nationality.ID) === '904576';
							}
							else {
								return true;
							}
						})
						.map((row) => ({
							label: row.DESCRIPTION,
							labelAr: row.DESCRIPTIONAR,
							value: String(row.ID),
						}))
				);
			}

			// Process titles data
			if (titlesData?.payload?.rows) {
				setTitleOptions(
					titlesData.payload.rows.map((row) => ({
						label: row.DESCRIPTION,
						value: String(row.ID),
						labelAr: row.DESCRIPTIONAR,
					}))
				);
			}

			// Process family size data
			if (familySizeData?.payload?.rows) {
				setDependentOptions(
					familySizeData.payload.rows.map((row) => ({
						label: row.DESCRIPTION, // Standardized field name
						value: String(row.ID), // Standardized field name
						labelAr: row.DESCRIPTIONAR, // Standardized field name
					}))
				);
			}

			// Process marital status data
			if (maritalStatusData?.payload?.rows) {
				setMaritalStatus(
					maritalStatusData.payload.rows.map((row) => ({
						label: row.DESCRIPTION, // Standardized field name
						labelAr: row.DESCRIPTIONAR, // Standardized field name
						value: String(row.ID), // Standardized field name
					}))
				);
			}

			// Process regions data
			if (regionsData?.payload?.rows) {
				setRegions(
					regionsData.payload.rows.map((row) => ({
						label: row.DESCRIPTION,
						value: String(row.ID),
						labelAr: String(row.DESCRIPTIONAR),
					}))
				);
			}
		} catch (error) {
			console.error('Error processing lookup data:', error);
			toast.error("Failed to load lookup data.");
		}
	};

	useEffect(() => {
		dispatch({ type: SET_ACTIVE_STEP_INDEX, payload: 0 });
	}, [dispatch]);

	useEffect(() => {
		dispatch({
			type: SET_STEP_VALIDITY,
			payload: {
				stepIndex: 0,
				valid: props.isValid,
			},
		});
	}, [dispatch, props.isValid]);


	// Effect to process lookup data when it becomes available
	useEffect(() => {
		if (!initialDataLoading) {
			const user = getUserId();
			initializeData(user);
			setTimeout(() => {
				// Personal data should be loaded after all details are fetched and loaded in case of Edit App
				getPersonalInfoAsync(user);
			}, 1500);
		}
	}, [initialDataLoading]); // Depend on loading state instead of empty dependency

	// Effect to process cities data when it changes
	useEffect(() => {
		if (citiesData?.payload?.rows) {
			setCities(
				citiesData.payload.rows.map((row) => ({
					label: row.DESCRIPTION,
					value: String(row.ID),
					labelAr: String(row.DESCRIPTIONAR),
				}))
			);
		}
	}, [citiesData]);


	useEffect(() => {
		if (state.personalDetails && state.editApplLoadFlags && !state.editApplLoadFlags?.personalDetails) {
			props.setValues((prevValues) => ({
				...prevValues,
				...state.personalDetails
			}))
			//flag change (EditData loaded once)
			dispatch({
				type: SET_EDIT_APPL_LOAD_FLAGS,
				payload: {
					...state.editApplLoadFlags,
					personalDetails: true
				},
			});
		} else {
			props.setValues({
				...state.personalDetails
			})
		}
	}, [state.editApplLoadFlags]);

	useEffect(() => {
		// Clear cities when region changes and refetch if needed
		setCities([]);
		if (props?.values?.region) {
			refetchCities();
		}
		// set city to empty
		if (valuesFilled) {
			props.setValues((values) => ({
				...values,
				city: null,
			}));
		}
		setValuesFilled(true);
	}, [props.values.region, refetchCities]);

	useEffect(() => {
		dispatch({
			type: PATCH_PERSONAL_DETAILS,
			payload: props.values,
		});
		// console.log(props.values)
	}, [dispatch, props.values]);

	const schema = useMemo(() => {
		return personalDetailsSchema({
			...fieldFlags,
			maritalStatus,
			nationalities,
			regions,
			cities,
			titleOptions,
			dependentOptions,
		});
	}, [maritalStatus, nationalities, regions, cities, fieldFlags]);

	if (initialDataLoading || personalInfoLoading) {
		return (
			<div className='p-5 bg-white flex flex-col gap-4'>
				<div className='flex gap-4'>
					<LoadingOutlined />
					<span>{translate("CREATE_REQUEST.LOADING_DETAILS")}</span>
				</div>
			</div>
		);
	}

	return (
		<>
			<h2 className='font-bold text-xl mb-4'>{translate("CREATE_REQUEST.PERSONAL_DETAILS")}</h2>
			<div className='mb-4'>
				<FormDrawer
					schema={{
						fields: schema,
					}}
				/>
			</div>
		</>
	);
});

export default PersonalDetails;
